import {
    buildUrl,
    endpointRegistry,
    EndpointRegistry,
    ExtractArguments,
    formatZodValidationError,
    HttpOptions,
    isRequestCancellation,
    NetworkError,
    RegisteredHttpEndpoint,
    ResponseParseError,
    ResponseWithMethod,
} from '@luminovo/api-contracts';
import { isProductionEnvironment } from '@luminovo/commons';
import * as Sentry from '@sentry/core';
import { z } from 'zod/v4';

import { getHttpClientConfig, resolveBase } from '../../config';
import { assertSuccessfulResponse } from './assertSuccessfulResponse';
import {
    composeRuntypeAssertionErrorMessage,
    MAX_ZOD_ERROR_LENGTH,
    truncate,
} from './composeRuntypeAssertionErrorMessage';
import { logToExternalErrorHandlers } from './logToExternalErrorHandlers';

// A wrong shape fails every fetch of that endpoint the same way, so one Sentry event per page load is enough.
const reportedSchemaMismatches = new Set<string>();

function assertResponseBodyShape({
    endpoint,
    url,
    method,
    requestBody,
    requestBodyRuntype,
    responseBody,
    responseBodyRuntype,
}: {
    endpoint: string;
    url: string;
    method: string;
    requestBody: unknown;
    requestBodyRuntype: z.ZodTypeAny;
    responseBody: unknown;
    responseBodyRuntype: z.ZodTypeAny;
}) {
    const startTime = performance.now();
    try {
        // Production only: in dev it would break local flows that have long tolerated mismatches.
        if (isProductionEnvironment()) {
            requestBodyRuntype.parse(requestBody);
        }

        responseBodyRuntype.parse(responseBody);
    } catch (runtypeError) {
        const msg = composeRuntypeAssertionErrorMessage({
            endpoint,
            url,
            method,
            requestBody,
            responseBody,
            runtypeError,
        });
        const ourError = new Error(msg);

        Sentry.logger.warn(Sentry.logger.fmt`http.schema_validation_failure endpoint=${endpoint} method=${method}`);
        if (!reportedSchemaMismatches.has(endpoint)) {
            reportedSchemaMismatches.add(endpoint);
            logToExternalErrorHandlers(ourError, {
                extra: {
                    zodError: truncate(
                        formatZodValidationError(runtypeError, { format: 'tree' }),
                        MAX_ZOD_ERROR_LENGTH,
                    ),
                },
            });
        }

        /* oxlint-disable-next-line no-console */
        console.error(msg);

        if (!isProductionEnvironment()) {
            throw ourError;
        }
    } finally {
        setRootSpanAttribute('httpClient.parsing_time', performance.now() - startTime);
    }
}

/** In milliseconds. Streamed spans drop `Sentry.setMeasurement`, which set the same value on the root span. */
function setRootSpanAttribute(name: string, valueMs: number) {
    const span = Sentry.getActiveSpan();
    if (span) {
        Sentry.getRootSpan(span).setAttribute(name, valueMs);
    }
}

function parseContent(response: Response) {
    if (response.headers.get('content-type')?.includes('application/json')) {
        return response.json();
    } else {
        return response.text();
    }
}

async function defaultResponseHandler(response: ResponseWithMethod, endpoint: string, requestBody: unknown) {
    await assertSuccessfulResponse(response, endpoint, requestBody);
    try {
        return await parseContent(response);
    } catch (error) {
        // Chromium rejects a JSON body too long for one string with a SyntaxError. A lost connection is a TypeError.
        if (error instanceof SyntaxError || error instanceof RangeError) {
            throw new ResponseParseError({ endpoint, cause: error });
        }
        throw error;
    }
}

export function deriveHeadersAndBody<T extends RegisteredHttpEndpoint>(
    unsafeOptions: ExtractArguments<T>,
    token?: string,
): { headers: HeadersInit; body: BodyInit | undefined } {
    const { requestBody } = unsafeOptions;
    let body: BodyInit | undefined = undefined;
    let headers: HeadersInit = {};

    // Set the token header if a token is provided.
    if (token) {
        headers['Authorization'] = `Bearer ${token}`;
    }

    // The user can override the default headers, including the token.
    for (const [k, v] of Object.entries(unsafeOptions.requestHeaders ?? {})) {
        if (v === undefined || v === null) {
            delete headers[k];
        } else {
            headers[k] = v;
        }
    }

    if ((requestBody as unknown) instanceof FormData) {
        body = requestBody as FormData;
    } else if (requestBody) {
        headers['Content-Type'] = 'application/json';
        body = JSON.stringify(requestBody);
    }

    return { headers, body };
}

/**
 * Sending one metric per API call used up the Sentry metrics quota by mid-month (SOU-5740). A sample keeps the
 * percentiles and the share of each status; counts in Sentry are a quarter of the real number.
 */
const RESPONSE_TIME_SAMPLE_RATE = 0.25;

function recordResponseTime(durationMs: number, attributes: { endpoint: string; method: string; status: string }) {
    if (Math.random() >= RESPONSE_TIME_SAMPLE_RATE) {
        return;
    }
    Sentry.metrics.distribution('http.response_time', durationMs, { unit: 'millisecond', attributes });
}

export async function http<T extends RegisteredHttpEndpoint>(
    endpoint: T,
    options: HttpOptions<T>,
    token?: string,
): Promise<z.infer<EndpointRegistry[T]['responseBody']>> {
    const span = Sentry.getActiveSpan();
    const startTime = performance.now();

    const unsafeOptions = options as ExtractArguments<T>;

    const endpointDef = endpointRegistry[endpoint];

    const { method, url } = buildUrl({
        rootUrl: resolveBase(endpointDef.base),
        endpoint,
        pathParams: unsafeOptions.pathParams ?? {},
        queryParams: unsafeOptions.queryParams ?? {},
        arrayFormat: endpointDef.arrayFormat,
    });

    const requestBody = unsafeOptions.requestBody;

    const { headers, body } = deriveHeadersAndBody(unsafeOptions, token);

    const response = await fetch(url, {
        method,
        headers,
        body,
        signal: unsafeOptions.signal,
    }).catch((e) => {
        // Before the metrics: nobody awaits the answer, so counting it would overstate how often requests fail.
        if (isRequestCancellation(e)) {
            throw e;
        }

        recordResponseTime(performance.now() - startTime, { endpoint, method, status: 'network_error' });

        // The request never got an answer (wifi, ad blocker): log the rate instead; beforeSend drops the NetworkError.
        if (e instanceof TypeError) {
            Sentry.logger.warn(Sentry.logger.fmt`http.network_error endpoint=${endpoint} method=${method}`, {
                endpoint,
                method,
                error: e.message,
            });
            throw new NetworkError({ endpoint });
        }

        logToExternalErrorHandlers(e, { extra: { endpoint } });
        throw e;
    });

    const responseHandler = endpointDef.handleResponse ?? defaultResponseHandler;
    const responseBody = await responseHandler(Object.assign(response, { method }), endpoint, requestBody);

    // `as any` keeps TypeScript from analysing the large response types, which slows down the IDE.
    assertResponseBodyShape({
        endpoint,
        url,
        method,
        requestBody,

        requestBodyRuntype: (endpointDef as any).requestBody,
        responseBody,

        responseBodyRuntype: (endpointDef as any).responseBody,
    });

    const endTime = performance.now();
    const status = String(response.status);

    recordResponseTime(endTime - startTime, { endpoint, method, status });

    span?.setAttributes({
        'httpClient.method': method,
        'httpClient.endpoint': endpoint,
        'httpClient.code': status,
        'httpClient.status_text': response.statusText,
        'httpClient.host_name': getHttpClientConfig().hostName,
    });
    setRootSpanAttribute('httpClient.total_time', endTime - startTime);

    return responseBody;
}
